import { FunctionComponent, useState } from "react";
import { FormInstance } from "antd";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building, Hash } from "lucide-react";

// Swedish organization number regex: XXXXXX-XXXX
// - 6 digits, hyphen, 4 digits
// - Third digit must be >= 2 (distinguishes from personal numbers)
const ORG_NUMBER_REGEX = /^\d{2}[2-9]\d{3}-\d{4}$/;

// Validate Swedish organization number using Luhn algorithm
function isValidSwedishOrgNumber(value: string): boolean {
  // Remove hyphen for validation
  const digits = value.replace(/-/g, '');
  
  // Must be exactly 10 digits
  if (!/^\d{10}$/.test(digits)) return false;
  
  // Third digit must be >= 2
  if (parseInt(digits[2]) < 2) return false;
  
  // Luhn algorithm validation
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    let digit = parseInt(digits[i]);
    // Multiply every other digit by 2, starting from first
    if (i % 2 === 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  
  return sum % 10 === 0;
}

// Auto-format organization number with hyphen
function formatOrgNumber(value: string): string {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Add hyphen after 6 digits
  if (digits.length > 6) {
    return `${digits.slice(0, 6)}-${digits.slice(6, 10)}`;
  }
  return digits;
}

interface BusinessFormProps {
  formRef: FormInstance<any>;
  handleClick: (formData: any) => void;
  errorMsg: string | null;
  loading: boolean;
}

const BusinessForm: FunctionComponent<BusinessFormProps> = ({
  handleClick,
  loading,
  errorMsg,
}) => {
  const [formData, setFormData] = useState({
    company: '',
    companyNumber: ''
  });
  const [orgNumberError, setOrgNumberError] = useState<string | null>(null);

  const onFinish = (e: React.FormEvent) => {
    console.log('🔵 Form submitted', { formData, loading });
    e.preventDefault();
    
    // Require organization number
    if (!formData.companyNumber || formData.companyNumber.trim() === '') {
      console.log('❌ Org number required');
      setOrgNumberError("Organisationsnummer krävs");
      return;
    }
    
    // Check basic format
    if (!ORG_NUMBER_REGEX.test(formData.companyNumber)) {
      console.log('❌ Org number format invalid:', formData.companyNumber);
      setOrgNumberError("Ange organisationsnummer i formatet XXXXXX-XXXX");
      return;
    }
    
    // Validate check digit (Luhn algorithm)
    if (!isValidSwedishOrgNumber(formData.companyNumber)) {
      console.log('❌ Org number Luhn validation failed:', formData.companyNumber);
      setOrgNumberError("Organisationsnumret verkar inte vara korrekt. Kontrollera att du angett rätt nummer.");
      return;
    }
    
    console.log('✅ Validation passed, calling handleClick');
    setOrgNumberError(null);
    handleClick(formData);
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === 'companyNumber') {
      // Auto-format organization number
      const formatted = formatOrgNumber(value);
      setFormData(prev => ({ ...prev, [field]: formatted }));
      
      // Clear error when user starts typing
      if (orgNumberError) setOrgNumberError(null);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-white/90 mb-2">
          Välkommen till Graftus!
        </h2>
        <h3 className="text-lg font-medium text-white/80 mb-3">
          Berätta lite mer om ditt företag.
        </h3>
        <p className="text-white/70 text-sm mb-2">
          Fortsätt genom att fylla i dina uppgifter och låt oss hjälpa dig att
          hitta de bästa säljarna för ditt företag.
        </p>
        <p className="text-white/70 text-sm">
          Vi ser fram emot att arbeta med dig och göra din rekryteringsprocess
          så smidig och effektiv som möjligt.
        </p>
      </div>

      {errorMsg && (
        <div className="rounded-xl border border-[rgba(255,107,107,0.3)] bg-[rgba(255,107,107,0.1)] p-4">
          <div className="text-[#ff6b6b] font-semibold text-sm">
            {errorMsg}
          </div>
        </div>
      )}

      <form onSubmit={onFinish} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="company" className="text-white/80 text-sm font-medium">
            Företagsnamn
          </Label>
          <div className="relative">
            <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#a8b8ff]" />
            <Input
              id="company"
              value={formData.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
              placeholder="Företag"
              autoComplete="organization"
              className="pl-10 bg-white/90 border-white/20 rounded-xl text-gray-900 h-12 text-base transition-all duration-300 hover:bg-white hover:border-[rgba(120,119,198,0.6)] hover:shadow-[0_0_0_3px_rgba(120,119,198,0.1)] focus:bg-white focus:border-[rgba(120,119,198,0.6)] focus:shadow-[0_0_0_3px_rgba(120,119,198,0.1)] placeholder:text-gray-500"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="companyNumber" className="text-white/80 text-sm font-medium">
            Organisationsnummer
          </Label>
          <div className="relative">
            <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#a8b8ff]" />
            <Input
              id="companyNumber"
              value={formData.companyNumber}
              onChange={(e) => handleInputChange('companyNumber', e.target.value)}
              placeholder="556123-4567"
              autoComplete="off"
              maxLength={11}
              className={`pl-10 bg-white/90 border-white/20 rounded-xl text-gray-900 h-12 text-base transition-all duration-300 hover:bg-white hover:border-[rgba(120,119,198,0.6)] hover:shadow-[0_0_0_3px_rgba(120,119,198,0.1)] focus:bg-white focus:border-[rgba(120,119,198,0.6)] focus:shadow-[0_0_0_3px_rgba(120,119,198,0.1)] placeholder:text-gray-500 ${orgNumberError ? 'border-red-500 focus:border-red-500' : ''}`}
            />
          </div>
          {orgNumberError && (
            <p className="text-red-400 text-sm mt-1">{orgNumberError}</p>
          )}
        </div>

        <Button
          type="submit"
          disabled={loading}
          onClick={() => {
            console.log('🟢 Button clicked!', { loading, disabled: loading });
            // Don't prevent default here - let form handle submission
          }}
          className="w-full bg-gradient-to-r from-[#667eea] to-[#764ba2] hover:from-[#5a6fd8] hover:to-[#6a4190] border-none rounded-xl h-12 text-base font-semibold transition-all duration-300 hover:transform hover:-translate-y-0.5 hover:shadow-[0_10px_25px_rgba(102,126,234,0.3)] active:transform-none flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          {loading ? "Skapar konto..." : "Skapa företagskonto"}
        </Button>
      </form>
    </div>
  );
};

export default BusinessForm;
